import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
// Keep original types for transformation target if components rely on them.
// Original Masternode and RewardStats types would have been in a types file or here.
// For this refactor, we'll redefine MasternodeWithStats based on what components need and what DB provides.

// Interface for data coming from Supabase (snake_case from 'masternodes' table)
interface DbMasternode {
  addr: string; // PK
  rank?: number | null;
  network_status?: string | null; // Original 'status'
  protocol_version?: number | null; // Original 'version'
  last_seen?: string | null; // ISO string date
  active_time?: number | null; // seconds
  tx_hash?: string | null;
  out_idx?: number | null;
  balance?: number | null;
  tier?: number | null;
  last_reward_paid_at?: string | null; // ISO string date
  rewards_per_day?: number | null;
  average_reward_interval_seconds?: number | null;
  is_active?: boolean | null;
  // first_seen_at and last_updated_at are also in DB but not directly used in MasternodeWithStats usually
}

// This is the type the components expect from the hook.
// Adjust if necessary based on actual component usage.
// Assuming original Masternode interface had: rank, network (unused), txhash, outidx, status, addr, version, lastseen (unix), activetime
// And RewardStats had: lastPaid (unix), rewardsPerDay, averageInterval
export interface MasternodeWithStats {
  addr: string;
  rank?: number;
  status?: string; // from network_status
  version?: number; // from protocol_version
  lastseen?: number; // Unix timestamp
  activetime?: number;
  txhash?: string;
  outidx?: number;
  balance?: number;
  tier?: number;
  rewardStats?: {
    lastPaid: number | null; // Unix timestamp
    rewardsPerDay: number;
    averageInterval: number;
  };
  // Other original Masternode fields if needed by components
}

const REFETCH_INTERVAL_MS = 1 * 60 * 1000; // Check every 1 minute
const MAX_DATA_AGE_MS = 3 * 60 * 1000;   // Refetch if data is older than 3 minutes

// Helper to transform DB data (single node) to MasternodeWithStats
const transformDbNodeToMasternodeWithStats = (dbNode: DbMasternode | null): MasternodeWithStats | null => {
  if (!dbNode || !dbNode.addr) {
    return null;
  }

  // Convert ISO date strings to Unix timestamps (seconds)
  const lastSeenUnix = dbNode.last_seen ? Math.floor(new Date(dbNode.last_seen).getTime() / 1000) : undefined;
  const lastRewardPaidAtUnix = dbNode.last_reward_paid_at ? Math.floor(new Date(dbNode.last_reward_paid_at).getTime() / 1000) : null;

  return {
    addr: dbNode.addr,
    rank: dbNode.rank ?? undefined,
    status: dbNode.network_status ?? undefined,
    version: dbNode.protocol_version != null ? Number(dbNode.protocol_version) : undefined,
    lastseen: lastSeenUnix,
    activetime: dbNode.active_time ?? undefined,
    txhash: dbNode.tx_hash ?? undefined,
    outidx: dbNode.out_idx ?? undefined,
    balance: dbNode.balance != null ? Number(dbNode.balance) : undefined,
    tier: dbNode.tier != null ? Number(dbNode.tier) : undefined,
    rewardStats: {
      lastPaid: lastRewardPaidAtUnix,
      rewardsPerDay: Number(dbNode.rewards_per_day) || 0,
      averageInterval: Number(dbNode.average_reward_interval_seconds) || 0,
    },
  };
};

export function useMasternodes() {
  const [nodes, setNodes] = useState<MasternodeWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastFetchTimestamp, setLastFetchTimestamp] = useState<number>(0);

  const fetchData = useCallback(async (isInitialLoad = false) => {
    if (!isInitialLoad) {
      console.log('[useMasternodes] Background refetch triggered...');
    }
    setError(null);
    try {
      const { data: dbNodes, error: dbError } = await supabase
        .from('masternodes')
        .select('*')
        .eq('is_active', true)
        .order('last_reward_paid_at', { ascending: false, nullsFirst: false });

      if (dbError) {
        console.error('[useMasternodes] Error fetching masternodes from Supabase:', dbError);
        if (isInitialLoad) setNodes([]); // Clear nodes on initial load error
        setError(new Error('Failed to fetch masternodes: ' + dbError.message));
      } else if (dbNodes) {
        const transformedNodes = dbNodes
          .map(transformDbNodeToMasternodeWithStats)
          .filter(node => node !== null) as MasternodeWithStats[];
        setNodes(transformedNodes);
        setLastFetchTimestamp(Date.now());
        console.log('[useMasternodes] Fetched', transformedNodes.length, 'masternodes');
      } else {
        if (isInitialLoad) setNodes([]);
      }
    } catch (err) {
      console.error('[useMasternodes] Catch block error fetching masternodes:', err);
      if (isInitialLoad) setNodes([]);
      setError(err instanceof Error ? err : new Error('An unexpected error occurred'));
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      }
    }
  }, []);

  // Initial fetch and real-time subscription
  useEffect(() => {
    let mounted = true;
    let channel: ReturnType<typeof supabase.channel> | null = null;

    const setupSubscription = async () => {
      // Initial fetch
      setLoading(true);
      await fetchData(true);

      if (!mounted) return;

      // Set up real-time subscription
      channel = supabase
        .channel('public:masternodes')
        .on(
          'postgres_changes',
          { 
            event: '*', 
            schema: 'public', 
            table: 'masternodes'
          },
          (payload) => {
            if (!mounted) return;
            console.log('[useMasternodes] Realtime update received:', payload);
            
            // For masternodes, it's easier to refetch all data on any change
            // since we need to maintain proper ordering by last_reward_paid_at
            fetchData();
          }
        )
        .subscribe((status, err) => {
          if (!mounted) return;
          if (err) {
            console.error('[useMasternodes] Realtime subscription error:', err);
          } else if (status === 'SUBSCRIBED') {
            console.log('[useMasternodes] Successfully subscribed to masternodes updates');
          }
        });
    };

    setupSubscription();

    return () => {
      mounted = false;
      if (channel) {
        // Use timeout to ensure cleanup happens after any pending operations
        const channelToRemove = channel;
        setTimeout(() => {
          supabase.removeChannel(channelToRemove).catch(err => 
            console.error('[useMasternodes] Failed to remove channel:', err)
          );
        }, 0);
      }
    };
  }, [fetchData]);

  // Periodic refetch if data is stale and tab is visible (as fallback)
  useEffect(() => {
    const intervalId = setInterval(() => {
      if (!document.hidden) {
        if (Date.now() - lastFetchTimestamp > MAX_DATA_AGE_MS) {
          console.log('[useMasternodes] Data is stale, refetching due to interval...');
          fetchData();
        }
      }
    }, REFETCH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [lastFetchTimestamp, fetchData]);

  // Refetch on window focus if data is stale (as fallback)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        if (Date.now() - lastFetchTimestamp > MAX_DATA_AGE_MS) {
          console.log('[useMasternodes] Tab focused and data is stale, refetching...');
          fetchData();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [lastFetchTimestamp, fetchData]);

  return { nodes, loading, error };
}